<script setup lang="ts">
import DocumentDetailIcon from './icons/DocumentDetailIcon.vue';
import { formatMobileTransactionDate } from '~/utilities/data-transformers';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { useSiteStore } from '~/stores/site.store';

interface IHeader {
  id: string;
  class?: string;
  currency?: boolean;
}

interface ITableGame {
  date: Date;
  gameName: string;
  amount: number;
  betId?: number;
}

interface IData {
  games: Array<ITableGame>;
}

//🧹🧹🧹🧹🧹🧹
interface ITableProps {
  headers: Array<IHeader>;
  mobileHeaders: Array<IHeader>;
  originalData: Array<IData> | any;
  totalPages: number;
  currentPage: number;
  recordsPerPage: number;
  searchOnHeader?: string; //what header should we query the search on (if we need to dynamically set it)
  mobileBP?: string; //Mobile breakpoint using tailwind classes e.g. sm, md, lg
  totalResults: number;
  clickable: boolean;
}

const site = useSiteStore();

const props = withDefaults(defineProps<ITableProps>(), {
  searchOnHeader: 'Type',
  recordsPerPage: 10,
  mobileBP: 'lg',
});

const emits = defineEmits([
  'changeCategory', //🧹
  'refresh',
  'updatePage',
  'search', //🧹
  'rowClick',
]);

const categoryData = reactive({ game: [] }); //data filtered by category selected
let sortedData = reactive({ record: [] });
let searchData = reactive({ record: [] });
let currentPageData = reactive({ record: [] }); //categoryData that is displayed on screen (10 a time + could be sorted)
const breakpoint = useBreakpoint();

//initialize dataset
function readData() {
  const data = [...props.originalData];
  categoryData.game = [...data];
  sortedData.record = [...categoryData.game];
  searchData.record = [...sortedData.record];

  updatePage();
}

//update current page
function updatePage() {
  if (searchData.record.length) {
    const start = (props.currentPage - 1) * props.recordsPerPage;
    const temp = props.currentPage * props.recordsPerPage;
    let end = start + props.recordsPerPage;
    if (temp > props.totalResults) {
      end = props.totalResults;
    }
    emits('updatePage', start + 1, end);
    currentPageData.record = searchData.record;
  } else {
    currentPageData.record = searchData.record;
    emits('updatePage', 0, 0, 1);
  }
}

function rowClicked(value) {
  if (props.clickable) {
    emits('rowClick', value);
  }
}
onMounted(() => {
  readData();
});

watch(
  () => props.currentPage,
  () => {
    updatePage();
  },
);
</script>
<template>
  <div class="transaction-summary sm:rounded-t-lg">
    <table class="shaded-table table-fixed">
      <thead>
        <tr>
          <td
            v-for="(header, index) in breakpoint.lgOrLess.value
              ? props.mobileHeaders
              : props.headers"
            :key="index"
            class="w-28 cell-width"
          >
            <div class="flex">
              <p class="text-sm font-bold my-0">{{ $t('jpc-' + header.id) }}</p>
            </div>
          </td>
        </tr>
      </thead>
      <tbody class="font-normal block allow-scroll">
        <tr
          v-for="(record, index) in currentPageData.record"
          :key="index"
          class="text-xs font-normal flex flex-row pl-2 pr-1 py-1 line-height-2 items-center"
        >
          <td v-if="!breakpoint.lgOrLess.value" class="cell-width">
            <div class="flex">
              <span class="">
                {{ record['transactionId'] }}
              </span>
            </div>
          </td>
          <td class="cell-width">
            <div class="flex">
              <span v-if="record['expires']" class="w-24">
                {{ formatMobileTransactionDate(record['expires']) }}
              </span>
              <span v-else class="w-28 cell-width">
                {{ formatMobileTransactionDate(record['date']) }}
              </span>
            </div>
          </td>

          <td class="td-truncate cell-width sm:pr-1">
            <div class="flex">
              <span class="truncate">
                {{ record['type'] ? record['type'] : record['gameName'] }}
              </span>
              <span
                v-if="record['gameName'] === 'Assign Bonus'"
                class="ml-1 cursor-pointer"
              >
                <InformationCircleIcon
                  class="text-gold-500 w-4 h-4"
                  @click="site.activateModal('transactionAssignBonus')"
                />
              </span>
            </div>
          </td>
          <td
            v-if="!breakpoint.lgOrLess.value"
            class="td-truncate cell-width sm:pr-2"
          >
            <div class="flex">
              <span class="truncate">
                {{ record['transactionType'] }}
              </span>
            </div>
          </td>
          <td class="px-0 relative" style="height: 14px">
            <span
              v-if="record['amount'] < 0"
              class="gold-gradient-text font-bold absolute top-0 left-[-8px]"
            >
              -&nbsp;
            </span>
            <p
              class="my-0 text-nowrap"
              style="line-height: 15px"
              v-html="useFormatCurrency(Math.abs(record['amount']))"
            />
          </td>

          <td
            class="ml-auto mr-0"
            @click="rowClicked(record[props.headers[0].id])"
          >
            <div
              class="px-0.5 showDetail cursor-pointer bg-light-300 dark:bg-dark-900 rounded-md flex justify-center items-center flex-wrap"
            >
              <DocumentDetailIcon />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <slot name="pagination" class="m-2" />
</template>

<style scoped lang="scss">
.allow-scroll {
  @apply overflow-y-auto overflow-x-hidden max-h-[50vh] xxsm:max-h-[60vh] md:max-h-[55vh] lg:max-h-[50vh] xl:max-h-[45vh];
}
.showDetail {
  @apply p-[3px] w-6 h-6;
}
.td-truncate {
  @apply block overflow-hidden text-ellipsis whitespace-nowrap w-28 pl-0;
}
.cell-width {
  @apply min-w-[32vw] sm:min-w-[35vw] md:min-w-[20vw] lg:min-w-[12vw] xl:min-w-[14vw];
}
td:nth-child(3),
th:nth-child(3) {
  @apply ml-3 sm:ml-2 lg:ml-0;
}
td:nth-child(5),
th:nth-child(5) {
  @apply sm:ml-2 md:ml-4;
}
</style>
